// ADR 5.9.26i §1/§2 — the route table. ADR 5.9.26p §5 — its guards.
//
// A v6 *data* router. 5.9.26i chose it for loaders; the guards below are
// deliberately **not** loaders, because the thing they have to read — the access
// JWT and the evaluated flags — lives in memory in a React context (§3), which
// a loader running outside the tree cannot see. A guard that read
// `localStorage` instead would answer "has a refresh token", which is a
// different question and the wrong one: it is true for a refresh token the
// server has already revoked.
//
// So each guard is a pathless route whose element renders `<Outlet />` or a
// `<Navigate />`, and the ordering that matters is expressed as nesting:
// signed-in wraps verified, and `/verify` and `/account` sit inside the first
// but outside the second — an unverified learner has to be able to reach the
// notice that explains why, and the account page that offers a way out.
//
// Deep links are served by `public/_redirects` (5.9.26k's file). Vite's dev and
// preview servers already fall back to index.html, so nothing here needs a
// history-fallback of its own.

import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
  type RouteObject,
} from 'react-router-dom'

import { accountRoute } from './accountRoutes'
import { NotFoundPage } from './NotFoundPage'
import { RouteFailure } from './RouteFailure'
// ADR 8.10.26u §3 — the builder and admin console leaves are `consolePage`, which
// brings the `console` namespace with the page chunk; `consoleRoutes.test.tsx` pins the set.
import { consolePage, page, safeNextPath, titled, withApps } from './routeHelpers'
import { Shell } from './Shell'
import { SIGNED_IN_ROUTE_ID, signInPath } from './signedOutEntry'
import { AuthSplit } from '../auth/AuthSplit'
import { RegisterPanel } from '../auth/RegisterPanel'
import { SignInPanel } from '../auth/SignInPanel'
import { VerifyEmailNotice } from '../auth/VerifyEmailNotice'
import { useAuthSession } from '../auth/session'
import { EmailPreferencesPage } from '../features/emailPreferences/EmailPreferencesPage'
import { Skeleton } from '../ui/Skeleton'

// ADR 24.9.26t §2 — the same column as `index.html`'s boot skeleton (its
// inline box in Tailwind spelling), so the handoff at first commit reads as one
// loader continuing. Only the first `Skeleton` carries the label: each is a
// live region, and four labelled ones would say "Loading…" four times.
//
// ADR 24.9.26ag §4 — exported for `main.tsx`, which gives it to
// `RouterProvider` as the first-load fallback: a deep link to a lazy route
// resolves that import before the first render, and with no fallback the router
// commits nothing, which `StaticShellCommit` still takes as its cue to remove
// the static shell — a blank page where a skeleton was.
export function SessionLoading() {
  const { t } = useTranslation()
  return (
    <div className="mx-auto mt-12 max-w-xl space-y-3 px-6">
      <Skeleton className="h-6 w-1/3" label={t('loading')} />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}

// Backend ADR 25.9.26n §5 — `safeNextPath` moved to `routeHelpers.ts` so the two
// sign-in panels can carry `next` on their cross-links without importing this
// file (which imports them). Re-exported: its suite and callers read it here.
export { safeNextPath }

/** §5's `requireSignedIn` — to `/signin?next=<where they were going>`. */
export function RequireSignedIn() {
  const { status } = useAuthSession()
  const location = useLocation()

  if (status === 'loading') return <SessionLoading />
  if (status === 'signedOut') return <Navigate to={signInPath(location.pathname, location.search)} replace />
  return <Outlet />
}

/** §5's `requireVerified` — to the notice, which re-asks on focus. */
export function RequireVerified() {
  const { emailVerified } = useAuthSession()
  return emailVerified ? <Outlet /> : <Navigate to="/verify" replace />
}

/**
 * The two doors, which are only doors while nobody is through them: a signed-in
 * visitor at `/signin` is sent on to wherever the guard was taking them.
 */
function SignedOutOnly({ panel }: { panel: ReactElement }) {
  const { status } = useAuthSession()
  const [params] = useSearchParams()

  if (status === 'loading') return <SessionLoading />
  if (status === 'signedIn') return <Navigate to={safeNextPath(params.get('next'))} replace />
  return <AuthSplit>{panel}</AuthSplit>
}

// ADR 1.10.26n §2 — `titled` and `page` live in `routeHelpers.ts`, shared with
// `accountRoutes.tsx`.
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Shell />,
    // ADR 8.9.26p §3 — on the root route, so it covers every descendant and
    // `Shell`/`AdaptiveShell` itself. An `errorElement` replaces *its own
    // route's* element, so a route that throws loses the chrome; that is the
    // opposite of the catch-all below and correct in both cases.
    errorElement: <RouteFailure />,
    children: [
      { path: 'signin', element: <SignedOutOnly panel={<SignInPanel />} />, handle: titled('auth_sign_in') },
      {
        path: 'register',
        element: <SignedOutOnly panel={<RegisterPanel />} />,
        handle: titled('auth_register'),
      },
      // ADR 5.9.26t §1 — the shared plan, **outside** every guard. Not merely
      // "not behind `RequireSignedIn`": it is not behind `SignedOutOnly`
      // either, because a signed-in reader following a link must land on the
      // plan rather than be bounced to `/plans`. The page decides what a viewer
      // may do from the DTO's `viewer` block; the router decides nothing.
      { path: 's/:token', lazy: page(() => import('../features/shared/SharedPlanPage'), 'SharedPlanPage'), handle: { ...titled('shared_plan_title'), frame: 'wide' } },
      // Backend ADR 24.9.26ab D6 — a lifecycle mail's footer link, outside
      // every guard for `s/:token`'s reason: the `?t=` token is the credential,
      // and its reader may never have signed in here.
      { path: 'email-preferences', element: <EmailPreferencesPage />, handle: titled('email_prefs_title') },
      // ADR 1.10.26c §5 — the trial mails' upgrade link: public likewise, lazy as a wasm leaf.
      { path: 'upgrade', lazy: page(() => import('../features/upgrade/UpgradePage'), 'UpgradePage'), handle: { ...titled('home_upgrade_cta'), frame: 'form' } },
      // ADR 8.10.26k §2 — the operator's on-device voice list: public and
      // unlinked, outside every guard so it opens in any Chrome tab on the phone.
      { path: 'diagnostics', lazy: page(() => import('../features/diagnostics/DiagnosticsPage'), 'DiagnosticsRoute'), handle: { ...titled('developer_tools_title'), frame: 'wide' } },
      // ADR 10.9.26h §3 — the vocab generator, reachable signed-in *and*
      // signed-out, so outside `RequireSignedIn` for `s/:token`'s reason: the
      // page decides what a stranger sees (since 11.9.26i §7 the controls and
      // generate on a device token, with `limit_create_account` under the
      // wall), the router decides nothing. Outside `RequireVerified` too: an
      // unverified learner's
      // generation is the server's call (`VerifiedClaims` is not on this
      // route), and a bounce to `/verify` would hide a screen they may use.
      // ADR 29.9.26am §3 — wide: at expanded the ask sits beside the results.
      {
        path: 'vocab',
        lazy: page(() => import('../features/vocab/VocabPage'), 'VocabPage'),
        handle: { ...titled('vocab_title'), frame: 'wide' },
      },
      {
        id: SIGNED_IN_ROUTE_ID, element: <RequireSignedIn />,
        children: [
          { path: 'verify', element: <VerifyEmailNotice />, handle: titled('verify_check_email') },
          accountRoute,
          // Backend ADR 25.9.26n §5/§12 — an app's consent screen: signed in, but
          // outside `RequireVerified`, whose redirect drops the URL; the page draws
          // its own verify-first state and carries on at the same address.
          { path: 'oauth/authorize', lazy: page(() => import('../features/oauthConsent/OAuthConsentPage'), 'OAuthConsentRoute'), handle: titled('oauth_consent_page_title') },
          // ADR 24.9.26ag §2 / 25.9.26f §3 — every page leaf loads on demand, through
          // the gate. ADR 5.9.26q — the lesson-plan surface, behind both guards. The
          // guard nesting is 5.9.26p's and is unchanged: these are routes, not
          // policy. `/plans/:id/play` is a sibling of the detail rather than a
          // child of it, because Play mode replaces the page rather than
          // appearing inside it.
          {
            element: <RequireVerified />,
            children: [
              // ADR 28.9.26e §2 — Home, the phone's first screen, led by its generator.
              // ADR 29.9.26am §2 — wide: at expanded the pinned lists sit beside it.
              // ADR 6.10.26l D5 — the header's Apps icon on the four main routes; Home
              // and Plans name the layout at which the page draws its own slot.
              {
                index: true,
                lazy: page(() => import('../features/home/HomePage'), 'HomePage'),
                handle: { ...titled('home_title'), frame: 'wide', ...withApps({ slot: 'home.side', drawnAt: { sizeClass: { expanded: true } } }) },
              },
              {
                path: 'plans',
                lazy: page(() => import('../features/plans/PlanListPage'), 'PlanListPage'),
                handle: { ...titled('lesson_plans_title'), ...withApps({ slot: 'plans.empty_detail', drawnAt: { paneLayout: { list_detail: true } } }) },
              },
              // ADR 28.9.26e §2 — the create form moved into Home; old links land there.
              { path: 'plans/new', element: <Navigate to="/" replace /> },
              // ADR 24.9.26l §3 — the page overrides this with the plan's title.
              {
                path: 'plans/:id',
                lazy: page(() => import('../features/plans/PlanDetailPage'), 'PlanDetailPage'),
                handle: { ...titled('lesson_plans_title'), frame: 'wide' },
              },
              // ADR 16.9.26o → 16.9.26p: the exercise left this route for the
              // detail page's set cards, and the Walkthrough — the feature
              // whose name the route carries — has it now.
              {
                path: 'plans/:id/play',
                lazy: page(() => import('../features/walkthrough/WalkthroughPage'), 'WalkthroughPage'),
                handle: { ...titled('walkthrough_title'), frame: 'wide' },
              },
              // ADR 5.9.26r — the deck, behind both guards like the plans.
              // React Router ranks the static `review` segment above the
              // dynamic `:listId`, so declaration order does not decide which
              // one `/flashcards/review` matches. Said here so nobody reorders
              // these three to find out.
              // ADR 5.9.26s §1 — the tutor, behind the same two guards. The
              // entitlement gate is *inside* the route element rather than
              // another pathless wrapper: two of its three verdicts render
              // (panel, teaser) and only the third redirects, which is a page's
              // decision to make, not a router's.
              { path: 'tutor', lazy: page(() => import('../features/tutor/TutorPanel'), 'TutorRoute'), handle: { ...titled('tutor_sheet_title'), ...withApps({}) } },
              {
                path: 'flashcards',
                lazy: page(() => import('../features/flashcards/ListsPage'), 'ListsPage'),
                handle: { ...titled('flashcard_title'), frame: 'wide', ...withApps({}) },
              },
              {
                path: 'flashcards/review',
                lazy: page(() => import('../features/flashcards/ReviewPage'), 'ReviewPage'),
                // ADR 28.9.26e §8 — immersive: no nav, so the header's arrow is the way out.
                handle: titled('flashcard_review_title', '/flashcards'),
              },
              // ADR 16.9.26t §1 — the management verbs' door, a second static
              // segment declared beside `review` for the ranking reason above.
              {
                path: 'flashcards/manage',
                lazy: page(() => import('../features/flashcards/ManageListsPage'), 'ManageListsPage'),
                handle: { ...titled('flashcard_lists_title'), frame: 'wide' },
              },
              {
                path: 'flashcards/:listId',
                lazy: page(() => import('../features/flashcards/CardsPage'), 'CardsPage'),
                handle: { ...titled('flashcard_title'), frame: 'wide' },
              },
              // ADR 23.9.26p §1 — API tokens, inside `RequireVerified` rather
              // than beside `account`: nobody needs a way out through a token
              // page, and W1's routes take `VerifiedClaims`. The flag gate is
              // inside the element, the tutor's pattern.
              // ADR 25.9.26k §1 — the `/admin` console joins it by the same
              // reasoning. ADR 25.9.26m deleted the token page; its old
              // bookmark still lands on the console.
              // ADR 2.10.26d §1 — the simple view the header opens; no frame is `prose`.
              { path: 'connections', lazy: page(() => import('../features/connections/ConnectionsPage'), 'ConnectionsRoute'), handle: titled('connections_title') },
              // ADR 2.10.26g §2 — the builder; the static `new` outranks `:flowId` whatever the order.
              // ADR 8.10.26b §3 — its landing is a console destination, so no back arrow (no destination carries one).
              { path: 'connections/build', lazy: consolePage(() => import('../features/builder/wizard/BuildPage'), 'BuildRoute'), handle: titled('builder_title') },
              { path: 'connections/build/new', lazy: consolePage(() => import('../features/builder/page/ConnectionPage'), 'NewConnectionRoute'), handle: { ...titled('builder_title', '/connections/build'), frame: 'full' } },
              // ADR 8.10.26h §1 — static `playground` outranks `:flowId` by segment score, and flow ids are UUIDs.
              { path: 'connections/build/playground', lazy: consolePage(() => import('../features/builder/playground/PlaygroundPage'), 'PlaygroundRoute'), handle: { ...titled('builder_playground_title', '/connections/build'), frame: 'wide' } },
              // ADR 8.10.26s §1 — every canvas, paged and filtered; static `canvases` outranks `:flowId` too.
              { path: 'connections/build/canvases', lazy: consolePage(() => import('../features/builder/list/CanvasListPage'), 'CanvasListRoute'), handle: { ...titled('builder_canvases_title', '/connections/build'), frame: 'wide' } },
              // ADR 15.10.26j §3 — the old canvas URLs redirect to the page's Canvas tab, router state carried.
              { path: 'connections/build/new/canvas', lazy: consolePage(() => import('../features/builder/page/ConnectionPage'), 'CanvasRedirectRoute'), handle: { ...titled('builder_title', '/connections/build'), frame: 'wide' } },
              { path: 'connections/build/:flowId/canvas', lazy: consolePage(() => import('../features/builder/page/ConnectionPage'), 'CanvasRedirectRoute'), handle: { ...titled('builder_title', '/connections/build'), frame: 'wide' } },
              // ADR 15.10.26j §1 — one page per connection, its tab in `?view=`.
              { path: 'connections/build/:flowId', lazy: consolePage(() => import('../features/builder/page/ConnectionPage'), 'ConnectionRoute'), handle: { ...titled('builder_title', '/connections/build'), frame: 'full' } },
              { path: 'admin', lazy: consolePage(() => import('../features/admin/AdminPage'), 'AdminRoute'), handle: { ...titled('developer_tools_title'), frame: 'wide' } },
              { path: 'admin/webhooks', lazy: consolePage(() => import('../features/admin/webhooks/WebhooksPage'), 'WebhooksRoute'), handle: { ...titled('admin_nav_webhooks'), frame: 'wide' } },
              { path: 'admin/apps', lazy: consolePage(() => import('../features/admin/apps/AppsPage'), 'AppsRoute'), handle: { ...titled('admin_nav_app_config'), frame: 'wide' } },
              // Backend ADR 25.9.26n §12 — Connected apps, a grant-only learner's console landing.
              { path: 'admin/connected-apps', lazy: page(() => import('../features/admin/connectedApps/ConnectedAppsPage'), 'ConnectedAppsRoute'), handle: { ...titled('admin_nav_connected_apps'), frame: 'wide' } },
              // ADR 30.9.26ap §5 — the marketplace spike's shelf; its two-flag gate is inside the element.
              { path: 'apps', lazy: page(() => import('../features/apps/shelf/ShelfPage'), 'ShelfRoute'), handle: { ...titled('apps_shelf_title'), frame: 'wide' } },
              { path: 'apps/install/:appId', lazy: page(() => import('../features/apps/InstallLinkPage'), 'InstallLinkRoute'), handle: { ...titled('apps_install_link_title'), frame: 'wide' } },
              { path: 'account/api-tokens', element: <Navigate to="/admin" replace />, handle: titled('developer_tools_title') },
            ],
          },
        ],
      },
      // ADR 8.9.26p §1 — the catch-all, **inside** the shell and **outside**
      // every guard. Inside, because the reader's problem is that they are
      // nowhere and `AdaptiveShell` is the thing that offers somewhere. Outside
      // the guards for the same reason `s/:token` is (5.9.26t §1): a signed-out
      // visitor who mistyped a URL should be told the page does not exist, not
      // bounced to `/signin` as though it did and they lacked permission.
      //
      // Declared last for readers, not for the matcher: React Router v6 *ranks*
      // matches, and a splat scores below every static and dynamic segment, so
      // this cannot shadow a real route wherever it is written. The same
      // ranking is why `/` still takes the index route above. Pinned by
      // 8.9.26p AC6, which resolves every declared path with `matchRoutes`.
      { path: '*', element: <NotFoundPage />, handle: titled('page_title_not_found') },
    ],
  },
]

export function createRouter() {
  return createBrowserRouter(routes)
}
